/**
 * This feature allows us to detect the percentage takeover for the advertiser with the highest win rate
 */
import configUtilities from "../../scriptsCore/js/configUtilities.js";
import dataLayerUtilities from "../../scriptsCore/js/dataLayerUtilities.js";
import events from "@trinitymirrordigital/chameleon-core/src/js/events.js";

const log = (...args) =>
  configUtilities.logFeature("sponsorshipDetector", ...args);

const scriptConfig = configUtilities.getScriptConfig();
const advertiserExclusionIds = scriptConfig?.advertiserExclusionIds || [
  32981537, //Google AdExchange
  4861612479, //Reach Adsense
  103255457, //Trinity Mirror AdSense
  80174537, //Google (HSK)
  95060777, //GOOGLE INC
  14290577, //GOOGLE
  87295937, //Mr Unfilled Impressions
  14719937, //Amazon
  4578381073, //AppNexus
  80177177, //AppNexus (HSK)
  71886977, //Teads
  4518234929, //Index Exchange (HB)
  4665485414, //Ozone Project
  59143937, //Rezonence
  4572516897, //SelectMedia
  14952617, //Criteo
  4842927965, //PubMatic
  85286777, //Just Premium
  14364017, //Rubicon
  81041177, //Pubmatic (HSK)
  80575697, //GumGum
  94567457, //33Across (HSK)
  4679158304, //Sharethrough
  106119737, //Insticator
  90985577, //Sublime Skinz
  99875177, //Collective
  4470217592, //Sovrn
];

function setAndReport(isSponsorship) {
  log("isSponsorship", isSponsorship);
  dataLayerUtilities.setInfo("isSponsorship", isSponsorship);

  events.fire(document.body, "isSponsorship.ready", isSponsorship);
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "isSponsorship.ready",
    isSponsorship: isSponsorship,
  });
}

/**
 * Counts number of ads per advertiser on page and gets the most prevalent advertiser, this triggers an event with the result being a true/false based on if the advertiser is more prevalent than backfillCount.
 * @function detectSponsorship
 */
export function detectSponsorship(data = {}, dependencies) {
  const { backfillCount, nonBackfilledAdSlots } = data;
  const { log } = dependencies;
  const lineItemIdCounts = {};
  const adslotTotal = (nonBackfilledAdSlots.length || 0) + (backfillCount || 0);
  let advTakeoverPercentage = 0;
  let numSlotsMostFrequentAdvertiser = 0;
  let scoreRequired = adslotTotal <= 5 ? 60 : 70;
  let isSponsorship = false;

  try {
    if (nonBackfilledAdSlots && nonBackfilledAdSlots.length > 0) {
      nonBackfilledAdSlots.forEach(function (i) {
        lineItemIdCounts[i] = (lineItemIdCounts[i] || 0) + 1;
      }); // how many nonBackfilledAdSlots  have the same lineItemId

      if (lineItemIdCounts) {
        numSlotsMostFrequentAdvertiser = nonBackfilledAdSlots.reduce(
          (max, slotName, i, arr) => {
            const numMatching = arr.reduce(
              (count, curr) => (slotName === curr ? count + 1 : count),
              0
            );
            return Math.max(numMatching, max);
          },
          0
        );

        advTakeoverPercentage = Math.round(
          (100 * numSlotsMostFrequentAdvertiser) / adslotTotal
        );
        isSponsorship = advTakeoverPercentage >= scoreRequired ? true : false;
      }

      log("Sponsorship Detector status:", {
        lineItemIdCounts,
        backfillCount,
        numSlotsMostFrequentAdvertiser,
        adslotTotal,
        scoreRequired: scoreRequired + "%",
        advTakeoverPercentage: advTakeoverPercentage + "%",
        isSponsorship,
      });
    }
  } catch (e) {
    log(e);
  }

  return isSponsorship;
}

export function getBackfillData(dependencies) {
  const {
    gptExpectedSlotCount,
    advertiserExclusionIds,
    log,
    events,
  } = dependencies;

  return new Promise((resolve) => {
    let backfillCount = 0;
    const nonBackfilledAdSlots = [];
    const gptPubAds = window.googletag.pubads();
    let count = 0;
    events.listen(gptPubAds, "slotResponseReceived", () => {
      count++;
      if (count === gptExpectedSlotCount) {
        gptPubAds.getSlots().forEach(function (slot) {
          var isBackfill =
            (slot &&
              slot.getResponseInformation() &&
              slot.getResponseInformation().isBackfill) ||
            "";
          var advertiserId =
            (slot &&
              slot.getResponseInformation() &&
              slot.getResponseInformation().advertiserId) ||
            "";
          var sourceAgnosticLineItemId =
            (slot &&
              slot.getResponseInformation() &&
              slot.getResponseInformation().sourceAgnosticLineItemId) ||
            "";

          var isAdvertiserBackfill =
            isBackfill || advertiserExclusionIds.includes(advertiserId)
              ? true
              : false;
          if (isAdvertiserBackfill) {
            log("advertiserIsBackfill: " + isAdvertiserBackfill, {
              advertiserId: advertiserId,
              sourceAgnosticLineItemId: sourceAgnosticLineItemId,
            });
            sourceAgnosticLineItemId = "isBackfill";
            backfillCount = backfillCount + 1;
            log("backfillCount:", backfillCount);
          } else if (sourceAgnosticLineItemId) {
            nonBackfilledAdSlots.push(sourceAgnosticLineItemId);
          }
        });
        resolve({
          backfillCount,
          nonBackfilledAdSlots,
        });
      }
    });
  });
}

/**
 * Gets all nonBackfilledAdSlots  and iterates over them to determine if the winning advertiser is considered backfill or not and then calls detectSponsorship Fn
 */
export function init() {
  const backfillDependencies = {
    log,
    events,
    advertiserExclusionIds,
    gptExpectedSlotCount: (
      configUtilities.getElementsByTypeForCurrentBreakpoint("gpt") || []
    )
      .filter((slot) => slot.isOOP !== true)
      .filter((slot) => slot.injectionEnabled !== false)
      .filter((slot) => slot.enabled !== false).length,
  };
  const detectorDependencies = { log };

  getBackfillData(backfillDependencies).then((data) => {
    const isSponsorship = detectSponsorship(data, detectorDependencies);
    setAndReport(isSponsorship);
  });
}

export default init;
